import clsx from 'clsx'
import { HugeiconsIcon } from '@hugeicons/react'
import { dateTime } from '@guestgoodie/helpers'
import { GraphQL } from '@guestgoodie/graphql'
import { useLingui } from '@lingui/react/macro'

import React, {
  ChangeEvent,
  CSSProperties,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import {
  Loading02Icon,
  PauseIcon,
  PlayIcon,
  VolumeHighIcon,
  VolumeOffIcon
} from '@hugeicons/core-solid-rounded'

import { GoodieFile, isGraphqlFile, isNewFile } from '../types'

const DURATION_FACTOR = 100

function VideoControl({
  videoRef,
  duration
}: {
  videoRef: React.RefObject<HTMLVideoElement | null>
  duration?: number
}) {
  const { t } = useLingui()
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [time, setTime] = useState(0)

  useEffect(() => {
    if (!videoRef.current) return

    videoRef.current.muted = isMuted
  }, [videoRef, isMuted])

  const onPlay = useCallback(
    (event: MouseEvent) => {
      event.preventDefault()

      if (!videoRef.current) return

      if (isPlaying) videoRef.current.pause()
      if (!isPlaying) videoRef.current.play()
    },
    [isPlaying, videoRef]
  )

  const onMute = useCallback(
    (event: MouseEvent) => {
      event.preventDefault()

      setIsMuted(muted => !muted)
    },
    [setIsMuted]
  )

  useEffect(() => {
    if (!videoRef.current) return

    const video = videoRef.current

    const play = () => setIsPlaying(true)
    const pause = () => setIsPlaying(false)

    const updateTime = () => {
      const newTime = (video.currentTime ?? 0) * DURATION_FACTOR

      setTime(Math.floor(newTime))
    }

    video.addEventListener('play', play)
    video.addEventListener('playing', play)
    video.addEventListener('pause', pause)
    video.addEventListener('timeupdate', updateTime)

    return () => {
      video.removeEventListener('play', play)
      video.removeEventListener('playing', play)
      video.removeEventListener('pause', pause)
      video.removeEventListener('timeupdate', updateTime)
    }
  }, [videoRef, setIsPlaying, setTime])

  const onChangeTime = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.preventDefault()

      if (!videoRef.current) return

      videoRef.current.pause()

      videoRef.current.currentTime =
        parseInt(event.target.value) / DURATION_FACTOR
    },
    [videoRef]
  )

  const timeLabel = useMemo(
    () => dateTime.formatSecondsToTime(Math.floor(time / DURATION_FACTOR)),
    [time]
  )

  return (
    <div className="w-full flex flex-col p-2 pb-1 gap-1 bg-black/50 rounded-t-small">
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row gap-2">
          <button
            className="size-4 shrink-0"
            type="button"
            onMouseDown={onPlay}
            tabIndex={0}
            aria-label={isPlaying ? t`Pause` : t`Play`}
          >
            <HugeiconsIcon
              icon={isPlaying ? PauseIcon : PlayIcon}
              className="size-full text-white"
            />
          </button>
          <button
            className="size-4 shrink-0"
            type="button"
            onMouseDown={onMute}
            tabIndex={0}
            aria-label={isMuted ? t`Unmute` : t`Mute`}
          >
            <HugeiconsIcon
              icon={isMuted ? VolumeHighIcon : VolumeOffIcon}
              className="size-full text-white"
            />
          </button>
        </div>
        <span className="text-white text-small shrink-0">{timeLabel}</span>
      </div>
      <div className="shrink-1 grow-1">
        <input
          className="w-full accent-primary bg-primary-light"
          type="range"
          onChange={onChangeTime}
          value={time}
          min={0}
          step={1}
          max={duration}
        />
      </div>
    </div>
  )
}

function VideoTrack({
  captions,
  videoRef,
  locale
}: {
  captions?: GraphQL.Caption[] | null
  videoRef: React.RefObject<HTMLVideoElement | null>
  locale?: GraphQL.AppLocale
}) {
  const cueRef = useRef<HTMLDivElement>(null)

  // We have to manually add the cue tracks, since we can't position them otherwise.
  // Also this way they look the same on all platforms
  const addCue = useCallback((index: number, text: string) => {
    if (!cueRef.current) return

    const span = document.createElement('p')
    span.innerText = text
    span.className = `cue-item cue-item-${index} max-w-[80%] rounded-small bg-black/60 px-2 py-1 text-center text-small text-white whitespace-pre-wrap`

    cueRef.current.append(span)
  }, [])

  const removeCue = useCallback((index: number) => {
    if (!cueRef.current) return

    const span = cueRef.current.querySelector(`.cue-item-${index}`)
    span?.remove()
  }, [])

  useEffect(() => {
    if (!videoRef.current || !captions) {
      return
    }

    const track =
      videoRef.current.textTracks.length > 0
        ? videoRef.current.textTracks[0]
        : videoRef.current.addTextTrack('captions')

    // Since we need to be able to edit the Cues on the fly, we should delete the old ones first
    Array.from(track.cues ?? []).forEach((cue, index) => {
      removeCue(index)
      track.removeCue(cue)
    })

    // Add all the cues to the captions
    captions.forEach(function (caption, index) {
      if (locale && caption.locale && caption.locale !== locale) return

      const cue = new VTTCue(caption.startAt, caption.endAt, caption.text)
      cue.onenter = () => addCue(index, cue.text)
      cue.onexit = () => removeCue(index)

      track.addCue(cue)
    })
  }, [videoRef, captions, locale, addCue, removeCue])

  return (
    <div
      ref={cueRef}
      className="w-full flex flex-col items-center justify-center gap-1 mb-1"
    />
  )
}

function Video({
  rootRef,
  value,
  src,
  ref
}: {
  rootRef: React.RefObject<HTMLDivElement | null>
  value: GoodieFile
  src: string
  ref: React.RefObject<HTMLVideoElement | null>
}) {
  const [scale, setScale] = useState<number>(1)
  const [videoHeight, setVideoHeight] = useState<number>()
  const [videoWidth, setVideoWidth] = useState<number>()

  useEffect(() => {
    if (isGraphqlFile(value) && value.state === GraphQL.FileState.Valid) return
    if (!value.processingMetadata?.cropping || !rootRef.current) return

    const frameHeight = rootRef.current?.clientHeight
    const { height } = value.processingMetadata.cropping
    const newScale = frameHeight / height

    setScale(newScale)
  }, [rootRef, value])

  useEffect(() => {
    if (!ref.current) return

    const video = ref.current

    const onSetVideoArea = () => {
      setVideoHeight(video.videoHeight)
      setVideoWidth(video.videoWidth)
    }

    video.addEventListener('loadeddata', onSetVideoArea)

    return () => {
      video.removeEventListener('loadeddata', onSetVideoArea)
    }
  }, [ref, rootRef, value, setVideoHeight, setVideoWidth])

  const videoStyle = useMemo(() => {
    if (isGraphqlFile(value) && value.state === GraphQL.FileState.Valid) return
    if (!value.processingMetadata?.cropping || !videoHeight || !videoWidth)
      return null

    const { x, y } = value.processingMetadata.cropping

    return {
      height: `${videoHeight * scale}px`,
      width: `${videoWidth * scale}px`,
      left: `${x * scale * -1}px`,
      top: `${y * scale * -1}px`
    } as CSSProperties
  }, [videoHeight, videoWidth, value, scale])

  return (
    <div
      className="absolute"
      style={
        videoStyle
          ? videoStyle
          : {
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }
      }
    >
      <video
        ref={ref}
        className={clsx('size-full object-cover')}
        controls={false}
        playsInline
        loop
        poster={
          !isNewFile(value) && value.remotePosterUrl
            ? value.remotePosterUrl
            : undefined
        }
      >
        <source src={src} />
      </video>
    </div>
  )
}

// One could ask why implement everything the Browser gives us for free
// It basically happened because we needed to show videos that are going to be cropped on the server already on the Frontend
// Since we can't actually crop them on the client we need to position the video in a way that it seems cropped however because of that the controls would be cut off or completely outside the bounding box
// This might all not be necessary when the object-view-box would already be baseline supported, but at the moment of writing this, it was only supported by 75% of browsers (Safari and Firefox not at all)
// However, in the future this can all be dumbed down to use `object-view-box` and we can delete a lot of unnecessary and stupid code
// See: https://caniuse.com/mdn-css_properties_object-view-box
export function VideoPreview(props: {
  value: GoodieFile
  src: string
  hideControls?: boolean
  locale?: GraphQL.AppLocale
}) {
  const { value, src, hideControls = false, locale } = props

  const rootRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  const [duration, setDuration] = useState<number>()

  useEffect(() => {
    if (!videoRef.current) return

    const video = videoRef.current

    const updateDuration = () => {
      setDuration(Math.floor(video.duration * DURATION_FACTOR))
    }

    video.addEventListener('loadeddata', updateDuration)

    return () => video.removeEventListener('loadeddata', updateDuration)
  }, [value, setDuration])

  const isProcessing = useMemo(() => {
    if (isNewFile(value)) return false
    return value.state === GraphQL.FileState.Processing
  }, [value])

  return (
    <div className="relative size-full" ref={rootRef}>
      <Video ref={videoRef} rootRef={rootRef} value={value} src={src} />
      {!hideControls && (
        <div className="absolute bottom-0 left-0 w-full flex flex-col">
          <VideoTrack
            videoRef={videoRef}
            captions={value.captions}
            locale={locale}
          />
          <VideoControl duration={duration} videoRef={videoRef} />
          {isProcessing && (
            <div className="flex flex-row items-center bg-black/50 px-2 pb-2 gap-2 text-white">
              <HugeiconsIcon
                icon={Loading02Icon}
                className="size-4 animate-spin"
              />
              <p className="text-small whitespace-pre-wrap w-full">
                Dieses Video wird gerade fürs Web optimiert
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
